import { Icons } from "assets";
import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import CustomPhoneInput from "shared/components/common/customPhoneInput";
import { toastMessage } from "shared/components/common/toast";
import ConfirmationModal from "shared/modal/confirmation";
import SuccessModal from "shared/modal/success";
import { setBreadCrumb } from "shared/redux/reducers/breadCrumbSlice";
import { routeConstant } from "shared/routes/routeConstant";
import {
  ActivateOrganizationService,
  DeleteOrganizationService,
  DisableOrganizationService,
  EditOrganizationService,
  GetOrganizationDetailService,
} from "shared/services/organization";
import { AddOrganizationVS } from "shared/utils/validation";
import styles from "./style.module.scss";
import { isValidPhoneNumber } from "react-phone-number-input";

const initialValues: OrganizationType = {
  name: "",
  email: "",
  address: "",
  contactNo: "",
  users: [
    {
      id: "",
      name: "",
      email: "",
      role: "",
    },
  ],
};

const EditOrganization = () => {
  const {
    theme: { theme },
  } = useSelector((state: any) => state?.root);

  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const organizationRef = useRef<any>(null);
  const selectedUserIndex = useRef<number>(0);

  const [isDisabled, setIsDisabled] = useState(false);
  const [showConfirmation, setConfirmation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isUserDelete, setIsUserDelete] = useState<boolean>(false);
  const [showSuccessModal, setSuccessModal] = useState<boolean>(false);

  const handleShowSuccessModal = () => {
    setSuccessModal(true);
  };

  const handleCloseSuccessModal = () => {
    navigate(routeConstant.organizations.path, { replace: true });
    setSuccessModal(false);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: AddOrganizationVS,
    onSubmit: () => {
      const emailOccurrences = values.users.reduce((acc: any, user, index) => {
        if (!acc[user.email]) {
          acc[user.email] = [];
        }
        acc[user.email].push(index);
        return acc;
      }, {});

      const duplicatedEmails = Object.keys(emailOccurrences).filter(
        (email) => emailOccurrences[email].length > 1
      );

      if (duplicatedEmails.length > 0) {
        duplicatedEmails.forEach((email) => {
          emailOccurrences[email].forEach((index: number) => {
            formik.setFieldError(
              `users[${index}].email`,
              "Email already in use"
            );
          });
        });
        setSubmitting(false);
      } else if (
        !isValidPhoneNumber(values.contactNo ? values.contactNo : "")
      ) {
        setSubmitting(false);
        setFieldError("contactNo", "Invalid Phone Number");
      } else {
        // If no duplicate emails, proceed with form submission logic
        handleAddOrganization();
      }
    },
  });
  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleChange,
    setFieldValue,
    handleSubmit,
    setSubmitting,
    setFieldError,
  } = formik;

  const handleAddUserForm = () => {
    setFieldValue("users", [
      ...values.users,
      {
        id: "",
        name: "",
        email: "",
        role: "",
      },
    ]);
  };

  const handleRemoveUserForm = () => {
    const newUsers = [...values.users];
    newUsers.splice(selectedUserIndex.current, 1);
    setFieldValue("users", newUsers);
    handleCloseConfirmation();
  };

  const handleOpenConfirmation = () => {
    setConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmation(false);
  };

  const handleDeleteOrganization = () => {
    setLoading(true);
    DeleteOrganizationService({ id: id ? id : "" })
      .then(() => {
        toastMessage("success", "Organization deleted successfully");
        navigate(-1);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setLoading(false);
        setConfirmation(false);
      });
  };

  const handleStatusOrganization = () => {
    setLoading(true);
    if (isDisabled) {
      ActivateOrganizationService({ id: id ? id : "" })
        .then(() => {
          toastMessage("success", "Organization enabled successfully");
          setIsDisabled(false);
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.error);
        })
        .finally(() => {
          setLoading(false);
          setConfirmation(false);
        });
    } else {
      DisableOrganizationService({ id: id ? id : "" })
        .then(() => {
          toastMessage("success", "Organization disabled successfully");
          setIsDisabled(true);
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.error);
        })
        .finally(() => {
          setLoading(false);
          setConfirmation(false);
        });
    }
  };

  const handleAddOrganization = () => {
    if (isValidPhoneNumber(values.contactNo ? values.contactNo : "")) {
      setSubmitting(true);
      let oldUsers = organizationRef.current?.users;
      let newUsers = values.users;
      let usersToAdd: any[] = [];
      let usersToDelete: any[] = [];
      let usersToUpdate: any[] = [];
      usersToAdd = newUsers.filter((newUser) => {
        return !oldUsers.find(
          (oldUser: any) => oldUser.email === newUser.email
        );
      });
      usersToDelete = oldUsers.filter((oldUser: any) => {
        return !newUsers.find(
          (newUser: any) => oldUser.email === newUser.email
        );
      });
      usersToUpdate = newUsers.filter((newUser: any) => {
        return oldUsers.find((oldUser: any) => oldUser.email === newUser.email);
      });

      let payload = {
        name: values.name,
        email: values.email,
        address: values.address,
        contactNo: values.contactNo,
        usersToAdd,
        usersToDelete,
        usersToUpdate,
      };

      EditOrganizationService(payload, id ? id : "")
        .then(() => {
          handleShowSuccessModal();
        })
        .catch((err) => {
          if (err?.response?.data?.errorTpe === 1) {
            toastMessage("error", "User Emails Already Exists");
            let data: any[] = err?.response?.data?.data;
            for (let i = 0; i < values.users?.length; i++) {
              let job = values.users[i];
              if (data.includes(job?.email)) {
                setFieldError(`users[${i}].email`, "Email already in use");
              }
            }
          } else {
            toastMessage("error", err?.response?.data?.error);
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      setSubmitting(false);
      setFieldError("contactNo", "Invalid Phone Number");
    }
  };

  const handleGetOrganizationDetail = () => {
    GetOrganizationDetailService(id ? id : "")
      .then(({ data: { data } }) => {
        if (data) {
          organizationRef.current = data;
          let users = data?.users;
          let newUsers: any = [];
          for (let i = 0; i < users.length; i++) {
            let user = users[i];
            newUsers.push({
              id: user?.id,
              name: user?.name,
              email: user?.email,
              role: user?.roleInOrganization,
            });
          }
          organizationRef.current.users = newUsers;
          setFieldValue("name", data?.name);
          setFieldValue("email", data?.email);
          setFieldValue("address", data?.address);
          setFieldValue("contactNo", data?.contactNo);
          setFieldValue("users", newUsers);
          setIsDisabled(data?.disabled);
        }
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
        navigate(-1);
      });
  };

  useEffect(() => {
    dispatch(
      setBreadCrumb({
        crumbs: [
          {
            title: routeConstant.editOrganization.title,
          },
        ],
      })
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id) {
      handleGetOrganizationDetail();
    } else {
      navigate(-1);
    }
    // eslint-disable-next-line
  }, [id]);

  return (
    <div className={classNames(styles.mainContainer)}>
      <div className={classNames("flex flex-col w-full gap-6")}>
        <h1>Organization Details</h1>
        <div className={classNames("flex flex-col gap-2")}>
          <div className={classNames("flex items-center gap-4")}>
            <CustomInput
              label="Organization Name"
              required
              placeholder="Enter Organization Name"
              value={values.name}
              onChange={handleChange("name")}
              error={
                touched.name &&
                //@ts-ignore
                errors?.name
                  ? errors?.name
                  : ""
              }
            />
            <CustomInput
              label="Email"
              required
              type="email"
              Icon={Icons.Mail}
              placeholder="Enter Email"
              value={values.email}
              onChange={handleChange("email")}
              error={
                touched.email &&
                //@ts-ignore
                errors?.email
                  ? errors?.email
                  : ""
              }
              readOnly
            />
          </div>
          <div className={classNames("flex items-center gap-4")}>
            <CustomInput
              label="Address"
              required
              placeholder="Enter Address"
              Icon={Icons.Pin}
              value={values.address}
              onChange={handleChange("address")}
              error={
                touched.address &&
                //@ts-ignore
                errors?.address
                  ? errors?.address
                  : ""
              }
            />
            <CustomPhoneInput
              label="Contact Info"
              required
              placeholder="405-235-3313"
              value={values.contactNo}
              onChange={(val) => {
                setFieldValue("contactNo", val);
              }}
              error={
                touched.contactNo &&
                //@ts-ignore
                errors?.contactNo
                  ? errors?.contactNo
                  : ""
              }
            />
          </div>
        </div>
        <h2>
          Add Associated <span>Users</span>
        </h2>
        <div>
          <div
            className={classNames(styles.tableWrapper)}
            onSubmit={(e) => e.preventDefault()}
          >
            <table
              className="table"
              style={{ minWidth: "800px", margin: "0px" }}
            >
              <thead>
                <tr style={{ height: "44px" }}>
                  <th className={classNames(styles.tableHead)}>
                    Full Name <span>*</span>
                  </th>
                  <th className={classNames(styles.tableHead)}>
                    Email <span>*</span>
                  </th>
                  <th className={classNames(styles.tableHead)}>
                    Role <span>*</span>
                  </th>
                  <th className={classNames(styles.tableHead)}>Action</th>
                </tr>
              </thead>
              <tbody>
                {values.users.map((user, index) => {
                  return (
                    <tr key={index} className="relative">
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "30%" }}
                      >
                        <CustomInput
                          required
                          placeholder="Enter Full Name"
                          value={user.name}
                          onChange={handleChange("users." + index + ".name")}
                          error={
                            touched.users?.[index]?.name &&
                            //@ts-ignore
                            errors.users?.[index]?.name
                          }
                          notRequiredMargin
                        />
                      </td>
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "30%" }}
                      >
                        <CustomInput
                          required
                          Icon={Icons.Mail}
                          type="email"
                          placeholder="Enter Email"
                          value={user.email}
                          onChange={handleChange("users." + index + ".email")}
                          error={
                            touched.users?.[index]?.email &&
                            //@ts-ignore
                            errors.users?.[index]?.email
                          }
                          notRequiredMargin
                          readOnly={user?.id ? true : false}
                        />
                      </td>
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "30%" }}
                      >
                        <CustomInput
                          placeholder="Enter Role"
                          value={user.role}
                          onChange={handleChange("users." + index + ".role")}
                          error={
                            touched.users?.[index]?.role &&
                            //@ts-ignore
                            errors.users?.[index]?.role
                          }
                          notRequiredMargin
                        />
                      </td>
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "10%" }}
                      >
                        {values.users.length > 1 ? (
                          <CustomButton
                            title="Remove"
                            Icon={Icons.TrashOutline2}
                            IconDirction="right"
                            containerStyle={classNames(styles.removeBtnStyle)}
                            iconStyle={classNames(styles.iconStyle)}
                            readOnly={values.users.length === 1}
                            onClick={() => {
                              if (values.users.length > 1) {
                                setIsDelete(false);
                                setIsUserDelete(true);
                                handleOpenConfirmation();
                                selectedUserIndex.current = index;
                              }
                            }}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <CustomButton
            title="Add More Users"
            Icon={Icons.Plus}
            containerStyle={classNames(
              theme === "dark" ? styles.btnStyle : styles.lighThemeBtnStyle,
              "gap-1 mt-3"
            )}
            IconDirction="left"
            onClick={handleAddUserForm}
          />
        </div>
        <div className={classNames("flex items-center gap-3 self-end ")}>
          <CustomButton
            title={!isDisabled ? "Disable" : "Enable"}
            containerStyle={classNames(
              !isDisabled ? styles.disableBtn : styles.enableBtn
            )}
            onClick={() => {
              setIsDelete(false);
              setIsUserDelete(false);
              handleOpenConfirmation();
            }}
          />
          <CustomButton
            title="Remove Organization"
            containerStyle={classNames(styles.removeBtnStyle)}
            onClick={() => {
              setIsDelete(true);
              setIsUserDelete(false);
              handleOpenConfirmation();
            }}
          />
          <CustomButton
            title="Submit"
            onClick={() => {
              handleSubmit();
            }}
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </div>
      <ConfirmationModal
        handleSubmit={() => {
          if (isUserDelete) {
            handleRemoveUserForm();
          } else if (isDelete) {
            handleDeleteOrganization();
          } else {
            handleStatusOrganization();
          }
        }}
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        subtitle={
          isUserDelete
            ? "Are you sure you want to Remove this user?"
            : `Are you sure you want to ${
                isDelete ? "Delete" : isDisabled ? "Enable" : "Disable"
              } this organization?`
        }
        actionButtonText={
          isUserDelete
            ? "Remove"
            : isDelete
            ? "Delete"
            : isDisabled
            ? "Enable"
            : "Disable"
        }
        loading={loading}
      />
      <SuccessModal
        title="Profile Update Successfully. "
        subtitle="Your Profile has been update successfully. Happy exploring..!"
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
      />
    </div>
  );
};

export default EditOrganization;
